import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  Mail, Lock, User, Phone, Globe, Languages, MapPin, Calendar,
  Loader2, CheckCircle2, Eye, EyeOff, AlertCircle, ArrowRight, ArrowLeft,
  Plane, Hotel, Mountain, Beach, Euro, Users as UsersIcon
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import PasswordStrengthIndicator from '@/components/PasswordStrengthIndicator';
import PhoneVerificationModal from '@/components/PhoneVerificationModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;

const TravelerMultiStepRegistrationForm = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');

  const [formData, setFormData] = useState({
    // Step 1: Account Info
    email: '',
    password: '',
    confirmPassword: '',
    nome: '',
    cognome: '',
    telefono: '',
    
    // Step 2: Travel Preferences
    destinazioni_preferite: [],
    tipo_alloggio_preferito: [],
    budget_medio: 'medio',
    frequenza_viaggio: 'occasionale',
    
    // Step 3: Personal Info
    data_nascita: '',
    paese_residenza: 'IT',
    citta_residenza: '',
    lingua_preferita: 'it',
    
    // Step 4: Legal & Verification
    consenso_tc: false,
    consenso_privacy: false,
    consenso_marketing: false
  });

  // Auto-save and restore form data
  useEffect(() => {
    const savedData = sessionStorage.getItem('travelerMultiStepForm');
    if (savedData) {
      try {
        const parsed = JSON.parse(savedData);
        setFormData({ ...formData, ...parsed });
      } catch (e) {
        console.error('Error loading saved form data:', e);
      }
    }
  }, []);

  useEffect(() => {
    const dataToSave = { ...formData };
    delete dataToSave.password;
    delete dataToSave.confirmPassword;
    sessionStorage.setItem('travelerMultiStepForm', JSON.stringify(dataToSave));
  }, [formData]);

  const countries = [
    { code: 'IT', name: 'Italia' },
    { code: 'FR', name: 'Francia' },
    { code: 'ES', name: 'Spagna' },
    { code: 'DE', name: 'Germania' },
    { code: 'GB', name: 'Regno Unito' },
    { code: 'US', name: 'Stati Uniti' },
    { code: 'CH', name: 'Svizzera' },
    { code: 'AT', name: 'Austria' },
    { code: 'NL', name: 'Paesi Bassi' },
    { code: 'BE', name: 'Belgio' }
  ];

  const languages = [
    { code: 'it', name: 'Italiano' },
    { code: 'en', name: 'English' },
    { code: 'fr', name: 'Français' },
    { code: 'es', name: 'Español' },
    { code: 'de', name: 'Deutsch' }
  ];

  const destinazioniOptions = [
    { id: 'mare', label: 'Mare', icon: '🏖️' },
    { id: 'montagna', label: 'Montagna', icon: '⛰️' },
    { id: 'citta', label: 'Città d\'Arte', icon: '🏛️' },
    { id: 'natura', label: 'Natura', icon: '🌳' },
    { id: 'avventura', label: 'Avventura', icon: '🧗' },
    { id: 'relax', label: 'Relax', icon: '🧘' },
    { id: 'cultura', label: 'Cultura', icon: '🎭' },
    { id: 'food', label: 'Enogastronomia', icon: '🍷' }
  ];

  const alloggioOptions = [
    { id: 'hotel', label: 'Hotel', icon: '🏨' },
    { id: 'bnb', label: 'B&B', icon: '🏠' },
    { id: 'appartamento', label: 'Appartamento', icon: '🏢' },
    { id: 'villa', label: 'Villa', icon: '🏘️' },
    { id: 'resort', label: 'Resort', icon: '🌴' },
    { id: 'ostello', label: 'Ostello', icon: '🛏️' }
  ];

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 1) {
      if (!formData.email) {
        newErrors.email = 'Email obbligatoria';
      } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
        newErrors.email = 'Email non valida';
      }

      if (!formData.password) {
        newErrors.password = 'Password obbligatoria';
      } else if (formData.password.length < 8) {
        newErrors.password = 'Password minimo 8 caratteri';
      } else if (!/[A-Z]/.test(formData.password)) {
        newErrors.password = 'Password deve contenere almeno una maiuscola';
      } else if (!/[a-z]/.test(formData.password)) {
        newErrors.password = 'Password deve contenere almeno una minuscola';
      } else if (!/[0-9]/.test(formData.password)) {
        newErrors.password = 'Password deve contenere almeno un numero';
      } else if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(formData.password)) {
        newErrors.password = 'Password deve contenere almeno un carattere speciale';
      }

      if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Le password non coincidono';
      }

      if (!formData.nome.trim()) {
        newErrors.nome = 'Nome obbligatorio';
      }

      if (!formData.cognome.trim()) {
        newErrors.cognome = 'Cognome obbligatorio';
      }

      if (!formData.telefono) {
        newErrors.telefono = 'Telefono obbligatorio';
      } else if (!/^\+?[0-9\s-]{8,}$/.test(formData.telefono)) {
        newErrors.telefono = 'Numero di telefono non valido';
      }
    }

    if (step === 3) {
      if (!formData.data_nascita) {
        newErrors.data_nascita = 'Data di nascita obbligatoria';
      }

      if (!formData.citta_residenza.trim()) {
        newErrors.citta_residenza = 'Città di residenza obbligatoria';
      }
    }

    if (step === 4) {
      if (!formData.consenso_tc) {
        newErrors.consenso_tc = 'Devi accettare i Termini e Condizioni';
      }

      if (!formData.consenso_privacy) {
        newErrors.consenso_privacy = 'Devi accettare la Privacy Policy';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(currentStep + 1);
      window.scrollTo(0, 0);
    }
  };

  const handleBack = () => {
    setCurrentStep(currentStep - 1);
    window.scrollTo(0, 0);
  };

  const toggleSelection = (array, item) => {
    if (array.includes(item)) {
      return array.filter(i => i !== item);
    }
    return [...array, item];
  };

  const handleSubmit = async () => {
    if (!validateStep(4)) {
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/register-traveler`, {
        email: formData.email,
        password: formData.password,
        nome: formData.nome,
        cognome: formData.cognome,
        telefono: formData.telefono,
        data_nascita: formData.data_nascita,
        paese_residenza: formData.paese_residenza,
        citta_residenza: formData.citta_residenza,
        lingua_preferita: formData.lingua_preferita,
        destinazioni_preferite: formData.destinazioni_preferite,
        tipo_alloggio_preferito: formData.tipo_alloggio_preferito,
        budget_medio: formData.budget_medio,
        frequenza_viaggio: formData.frequenza_viaggio,
        consenso_tc: formData.consenso_tc,
        consenso_privacy: formData.consenso_privacy,
        consenso_marketing: formData.consenso_marketing
      });

      console.log('Registration successful:', response.data);
      
      // Clear saved form data
      sessionStorage.removeItem('travelerMultiStepForm');
      
      // Show phone verification modal
      setRegisteredEmail(formData.email);
      setShowVerificationModal(true);

    } catch (error) {
      console.error('Registration error:', error);
      if (error.response?.data?.detail === 'Email already registered') {
        setErrors({ email: 'Questa email è già registrata' });
        setCurrentStep(1);
      } else {
        toast.error(error.response?.data?.detail || 'Errore durante la registrazione');
      }
    } finally {
      setLoading(false);
    }
  };

  const renderStepIndicator = () => (
    <div className="flex items-center justify-center mb-8">
      {[1, 2, 3, 4].map((step) => (
        <React.Fragment key={step}>
          <div className={`
            flex items-center justify-center w-10 h-10 rounded-full font-bold
            ${currentStep >= step 
              ? 'bg-blue-600 text-white' 
              : 'bg-slate-200 text-slate-400'}
            transition-all duration-300
          `}>
            {step}
          </div>
          {step < 4 && (
            <div className={`
              w-16 h-1 mx-2
              ${currentStep > step ? 'bg-blue-600' : 'bg-slate-200'}
              transition-all duration-300
            `} />
          )}
        </React.Fragment>
      ))}
    </div>
  );

  return (
    <>
      <Card className="max-w-3xl mx-auto backdrop-blur-sm bg-white/90 border border-slate-200 shadow-2xl">
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6 rounded-t-xl">
          <h2 className="text-2xl font-bold">Crea il tuo Account Viaggiatore</h2>
          <p className="text-blue-100 text-sm mt-1">
            Step {currentStep} di 4
          </p>
        </div>

        <div className="p-8">
          {renderStepIndicator()}

          {/* STEP 1: Account Info */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <h3 className="text-xl font-bold text-slate-900 mb-2">
                  Informazioni Account
                </h3>
                <p className="text-sm text-slate-600">
                  Crea le tue credenziali di accesso
                </p>
              </div>

              <div>
                <Label htmlFor="email" className="flex items-center gap-2 mb-2">
                  <Mail className="w-4 h-4 text-blue-600" />
                  Email *
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="tuo@email.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className={errors.email ? 'border-red-500' : ''}
                />
                {errors.email && <p className="text-xs text-red-600 mt-1">{errors.email}</p>}
              </div>

              <div>
                <Label htmlFor="password" className="flex items-center gap-2 mb-2">
                  <Lock className="w-4 h-4 text-blue-600" />
                  Password *
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Minimo 8 caratteri"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className={errors.password ? 'border-red-500' : ''}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {errors.password && <p className="text-xs text-red-600 mt-1">{errors.password}</p>}
                <PasswordStrengthIndicator password={formData.password} />
              </div>

              <div>
                <Label htmlFor="confirmPassword" className="flex items-center gap-2 mb-2">
                  <Lock className="w-4 h-4 text-blue-600" />
                  Conferma Password *
                </Label>
                <Input
                  id="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Ripeti password"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  className={errors.confirmPassword ? 'border-red-500' : ''}
                />
                {errors.confirmPassword && <p className="text-xs text-red-600 mt-1">{errors.confirmPassword}</p>}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="nome" className="flex items-center gap-2 mb-2">
                    <User className="w-4 h-4 text-blue-600" />
                    Nome *
                  </Label>
                  <Input
                    id="nome"
                    placeholder="Mario"
                    value={formData.nome}
                    onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                    className={errors.nome ? 'border-red-500' : ''}
                  />
                  {errors.nome && <p className="text-xs text-red-600 mt-1">{errors.nome}</p>}
                </div>

                <div>
                  <Label htmlFor="cognome" className="flex items-center gap-2 mb-2">
                    <User className="w-4 h-4 text-blue-600" />
                    Cognome *
                  </Label>
                  <Input
                    id="cognome"
                    placeholder="Rossi"
                    value={formData.cognome}
                    onChange={(e) => setFormData({ ...formData, cognome: e.target.value })}
                    className={errors.cognome ? 'border-red-500' : ''}
                  />
                  {errors.cognome && <p className="text-xs text-red-600 mt-1">{errors.cognome}</p>}
                </div>
              </div>

              <div>
                <Label htmlFor="telefono" className="flex items-center gap-2 mb-2">
                  <Phone className="w-4 h-4 text-blue-600" />
                  Telefono (con prefisso) *
                </Label>
                <Input
                  id="telefono"
                  type="tel"
                  placeholder="+39 333 1234567"
                  value={formData.telefono}
                  onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                  className={errors.telefono ? 'border-red-500' : ''}
                />
                {errors.telefono && <p className="text-xs text-red-600 mt-1">{errors.telefono}</p>}
              </div>

              <Button
                onClick={handleNext}
                className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 h-12"
              >
                Continua
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          )}

          {/* STEP 2: Travel Preferences */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <h3 className="text-xl font-bold text-slate-900 mb-2">
                  Preferenze di Viaggio
                </h3>
                <p className="text-sm text-slate-600">
                  Aiutaci a conoscere i tuoi gusti (opzionale)
                </p>
              </div>

              <div>
                <Label className="mb-3 block font-semibold">Destinazioni Preferite</Label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {destinazioniOptions.map(dest => (
                    <div
                      key={dest.id}
                      onClick={() => setFormData({
                        ...formData,
                        destinazioni_preferite: toggleSelection(formData.destinazioni_preferite, dest.id)
                      })}
                      className={`
                        p-3 border-2 rounded-lg cursor-pointer transition-all text-center
                        ${formData.destinazioni_preferite.includes(dest.id)
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-slate-200 hover:border-slate-300'}
                      `}
                    >
                      <div className="text-2xl mb-1">{dest.icon}</div>
                      <div className="text-xs font-medium text-slate-700">{dest.label}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <Label className="mb-3 block font-semibold">Tipo di Alloggio Preferito</Label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {alloggioOptions.map(alloggio => (
                    <div
                      key={alloggio.id}
                      onClick={() => setFormData({
                        ...formData,
                        tipo_alloggio_preferito: toggleSelection(formData.tipo_alloggio_preferito, alloggio.id)
                      })}
                      className={`
                        p-3 border-2 rounded-lg cursor-pointer transition-all text-center
                        ${formData.tipo_alloggio_preferito.includes(alloggio.id)
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-slate-200 hover:border-slate-300'}
                      `}
                    >
                      <div className="text-2xl mb-1">{alloggio.icon}</div>
                      <div className="text-xs font-medium text-slate-700">{alloggio.label}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="budget" className="flex items-center gap-2 mb-2">
                    <Euro className="w-4 h-4 text-blue-600" />
                    Budget Medio per Viaggio
                  </Label>
                  <select
                    id="budget"
                    value={formData.budget_medio}
                    onChange={(e) => setFormData({ ...formData, budget_medio: e.target.value })}
                    className="w-full h-10 px-3 rounded-md border border-slate-200 bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none"
                  >
                    <option value="basso">Economico (&lt;500€)</option>
                    <option value="medio">Medio (500€-1500€)</option>
                    <option value="alto">Alto (1500€-3000€)</option>
                    <option value="lusso">Lusso (&gt;3000€)</option>
                  </select>
                </div>

                <div>
                  <Label htmlFor="frequenza" className="flex items-center gap-2 mb-2">
                    <Plane className="w-4 h-4 text-blue-600" />
                    Frequenza Viaggi
                  </Label>
                  <select
                    id="frequenza"
                    value={formData.frequenza_viaggio}
                    onChange={(e) => setFormData({ ...formData, frequenza_viaggio: e.target.value })}
                    className="w-full h-10 px-3 rounded-md border border-slate-200 bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none"
                  >
                    <option value="raro">Raramente (1-2 volte/anno)</option>
                    <option value="occasionale">Occasionale (3-5 volte/anno)</option>
                    <option value="frequente">Frequente (6-10 volte/anno)</option>
                    <option value="molto_frequente">Molto Frequente (&gt;10 volte/anno)</option>
                  </select>
                </div>
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={handleBack}
                  variant="outline"
                  className="flex-1 h-12"
                >
                  <ArrowLeft className="w-5 h-5 mr-2" />
                  Indietro
                </Button>
                <Button
                  onClick={handleNext}
                  className="flex-1 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 h-12"
                >
                  Continua
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
            </div>
          )}

          {/* STEP 3: Personal Info */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <h3 className="text-xl font-bold text-slate-900 mb-2">
                  Informazioni Personali
                </h3>
                <p className="text-sm text-slate-600">
                  Completa il tuo profilo
                </p>
              </div>

              <div>
                <Label htmlFor="data_nascita" className="flex items-center gap-2 mb-2">
                  <Calendar className="w-4 h-4 text-blue-600" />
                  Data di Nascita *
                </Label>
                <Input
                  id="data_nascita"
                  type="date"
                  value={formData.data_nascita}
                  onChange={(e) => setFormData({ ...formData, data_nascita: e.target.value })}
                  className={errors.data_nascita ? 'border-red-500' : ''}
                />
                {errors.data_nascita && <p className="text-xs text-red-600 mt-1">{errors.data_nascita}</p>}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="paese" className="flex items-center gap-2 mb-2">
                    <Globe className="w-4 h-4 text-blue-600" />
                    Paese di Residenza *
                  </Label>
                  <select
                    id="paese"
                    value={formData.paese_residenza}
                    onChange={(e) => setFormData({ ...formData, paese_residenza: e.target.value })}
                    className="w-full h-10 px-3 rounded-md border border-slate-200 bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none"
                  >
                    {countries.map(country => (
                      <option key={country.code} value={country.code}>{country.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <Label htmlFor="citta_residenza" className="flex items-center gap-2 mb-2">
                    <MapPin className="w-4 h-4 text-blue-600" />
                    Città di Residenza *
                  </Label>
                  <Input
                    id="citta_residenza"
                    placeholder="Milano"
                    value={formData.citta_residenza}
                    onChange={(e) => setFormData({ ...formData, citta_residenza: e.target.value })}
                    className={errors.citta_residenza ? 'border-red-500' : ''}
                  />
                  {errors.citta_residenza && <p className="text-xs text-red-600 mt-1">{errors.citta_residenza}</p>}
                </div>
              </div>

              <div>
                <Label htmlFor="lingua" className="flex items-center gap-2 mb-2">
                  <Languages className="w-4 h-4 text-blue-600" />
                  Lingua Preferita *
                </Label>
                <select
                  id="lingua"
                  value={formData.lingua_preferita}
                  onChange={(e) => setFormData({ ...formData, lingua_preferita: e.target.value })}
                  className="w-full h-10 px-3 rounded-md border border-slate-200 bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none"
                >
                  {languages.map(lang => (
                    <option key={lang.code} value={lang.code}>{lang.name}</option>
                  ))}
                </select>
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={handleBack}
                  variant="outline"
                  className="flex-1 h-12"
                >
                  <ArrowLeft className="w-5 h-5 mr-2" />
                  Indietro
                </Button>
                <Button
                  onClick={handleNext}
                  className="flex-1 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 h-12"
                >
                  Continua
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
            </div>
          )}

          {/* STEP 4: Legal & Verification */}
          {currentStep === 4 && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <h3 className="text-xl font-bold text-slate-900 mb-2">
                  Legale e Verifica
                </h3>
                <p className="text-sm text-slate-600">
                  Ultimi passi per completare la registrazione
                </p>
              </div>

              {/* Riepilogo */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-2">
                <h4 className="font-semibold text-blue-900 mb-3">Riepilogo Registrazione</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-slate-600">Nome:</span>
                    <span className="ml-2 font-medium">{formData.nome} {formData.cognome}</span>
                  </div>
                  <div>
                    <span className="text-slate-600">Email:</span>
                    <span className="ml-2 font-medium">{formData.email}</span>
                  </div>
                  <div>
                    <span className="text-slate-600">Telefono:</span>
                    <span className="ml-2 font-medium">{formData.telefono}</span>
                  </div>
                  <div>
                    <span className="text-slate-600">Città:</span>
                    <span className="ml-2 font-medium">{formData.citta_residenza}</span>
                  </div>
                </div>
              </div>

              {/* Consensi */}
              <div className="space-y-4 pt-4 border-t border-slate-200">
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="tc"
                    checked={formData.consenso_tc}
                    onCheckedChange={(checked) => setFormData({ ...formData, consenso_tc: checked })}
                    className={errors.consenso_tc ? 'border-red-500' : ''}
                  />
                  <label htmlFor="tc" className="text-sm text-slate-700 leading-relaxed cursor-pointer">
                    Accetto i <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-semibold">Termini e Condizioni</a> di Facile Digitale Srls *
                  </label>
                </div>
                {errors.consenso_tc && <p className="text-xs text-red-600 ml-8">{errors.consenso_tc}</p>}

                <div className="flex items-start gap-3">
                  <Checkbox
                    id="privacy"
                    checked={formData.consenso_privacy}
                    onCheckedChange={(checked) => setFormData({ ...formData, consenso_privacy: checked })}
                    className={errors.consenso_privacy ? 'border-red-500' : ''}
                  />
                  <label htmlFor="privacy" className="text-sm text-slate-700 leading-relaxed cursor-pointer">
                    Accetto la <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-semibold">Privacy Policy</a> di Facile Digitale Srls *
                  </label>
                </div>
                {errors.consenso_privacy && <p className="text-xs text-red-600 ml-8">{errors.consenso_privacy}</p>}

                <div className="flex items-start gap-3">
                  <Checkbox
                    id="marketing"
                    checked={formData.consenso_marketing}
                    onCheckedChange={(checked) => setFormData({ ...formData, consenso_marketing: checked })}
                  />
                  <label htmlFor="marketing" className="text-sm text-slate-700 leading-relaxed cursor-pointer">
                    Voglio ricevere offerte e novità via email
                  </label>
                </div>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-sm text-amber-900">
                  📱 <strong>Verifica Telefonica:</strong> Dopo la registrazione, riceverai un codice OTP sul tuo telefono per verificare il numero.
                </p>
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={handleBack}
                  variant="outline"
                  className="flex-1 h-12"
                >
                  <ArrowLeft className="w-5 h-5 mr-2" />
                  Indietro
                </Button>
                <Button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="flex-1 bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 h-12 shadow-lg"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin mr-2" />
                      Registrazione...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="w-5 h-5 mr-2" />
                      Completa Registrazione
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Phone Verification Modal */}
      <PhoneVerificationModal
        open={showVerificationModal}
        onClose={() => setShowVerificationModal(false)}
        email={registeredEmail}
        onSuccess={(data) => {
          toast.success(' Registrazione completata con successo!');
          
          // Save token and user data
          localStorage.setItem('token', data.token);
          localStorage.setItem('user', JSON.stringify(data.user));
          
          setTimeout(() => {
            navigate('/traveler-dashboard');
          }, 1500);
        }}
      />
    </>
  );
};

export default TravelerMultiStepRegistrationForm;